// Primary-subdivision (county) selector. Loads the list once and uses MUI's
// Autocomplete for keyboard-accessible search + selection. On commit, kicks
// off the area-stats computation so the cards downstream update. The label
// noun comes from settings so it adapts to the region (county/parish/borough).
import { useEffect, useMemo } from 'react';
import { Autocomplete, TextField, CircularProgress } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import {
  fetchCounties, computeAreaStats, setAreaRefId,
} from '../store/area-slice.js';
import { selectSettings } from '../store/settings-slice.js';

const CountyDropdown = () => {
  const dispatch = useDispatch();
  const { data, loading, error } = useSelector((s) => s.area.counties);
  const areaRefId = useSelector((s) => s.area.areaRefId);
  const settings = useSelector(selectSettings);
  const noun = settings.subdivision_primary?.singular || 'county';

  useEffect(() => {
    if (!data && !loading) dispatch(fetchCounties());
  }, [data, loading, dispatch]);

  const options = useMemo(() => data || [], [data]);
  const selected = useMemo(
    () => options.find((c) => c.id === areaRefId) || null,
    [options, areaRefId],
  );

  return (
    <Autocomplete
      options={options}
      value={selected}
      loading={loading}
      getOptionLabel={(opt) => opt.name || ''}
      isOptionEqualToValue={(opt, val) => opt.id === val.id}
      onChange={(_e, value) => {
        const nextId = value ? value.id : null;
        dispatch(setAreaRefId(nextId));
        if (nextId != null) dispatch(computeAreaStats({ areaType: 'county', areaRefId: nextId }));
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          label={`Select ${noun}`}
          error={Boolean(error)}
          helperText={error || null}
          slotProps={{
            ...params.slotProps,
            input: {
              ...params.slotProps.input,
              endAdornment: (
                <>
                  {loading ? <CircularProgress color="inherit" size={18} /> : null}
                  {params.slotProps.input.endAdornment}
                </>
              ),
            },
          }}
        />
      )}
    />
  );
};

export default CountyDropdown;
